'use client'

import { useForm } from 'react-hook-form'
import { useStore } from '@/store'
import { LoginModal } from '../../../../components/MainLayout/Header/TopMenu/LoginModal'
import { Button } from '@/General/Button'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { Alert } from '@/General/Forms/Alert'
import { useRouter } from 'next/navigation'
import { useState, useEffect } from 'react'
import { useTurnstile } from '@/Turnstile'
import { useCreateBlogCommentMutation } from '@/generated/graphql'
import { ArrowPathIcon } from '@heroicons/react/24/outline'

interface CommentFormData {
  comment: string
}

interface CommentFormProps {
  blogPostId: number
}

export const CommentForm = ({ blogPostId }: CommentFormProps) => {
  const user = useStore(state => state.user)
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  const [loginModalMessage, setLoginModalMessage] = useState<string>()
  const [showSuccess, setShowSuccess] = useState(false)
  const router = useRouter()
  const { executeTurnstile } = useTurnstile()
  const [replyTo, setReplyTo] = useState<{
    id: number
    name: string
  } | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<CommentFormData>()

  const { mutate: createComment, isPending } = useCreateBlogCommentMutation({
    onSuccess: result => {
      if (result.createBlogComment) {
        reset()
        setReplyTo(null)
        setShowSuccess(true)
        router.refresh()
      } else {
        setError('root', {
          type: 'manual',
          message: 'Failed to post comment. Please try again.',
        })
      }
    },
    onError: (error: { message?: string; graphQLErrors?: Array<{ message?: string }> }) => {
      const serverMessage =
        error?.graphQLErrors?.[0]?.message ?? error?.message
      setError('root', {
        type: 'manual',
        message:
          serverMessage || 'Failed to post comment. Please try again.',
      })
    },
  })

  // Setup global reply function
  useEffect(() => {
    ;(window as any).replyToComment = (id: number, name: string) => {
      setReplyTo({ id, name })
      document.getElementById('comment-form')?.scrollIntoView({
        behavior: 'smooth',
      })
    }

    return () => {
      delete (window as any).replyToComment
    }
  }, [])

  const cancelReply = (e: React.MouseEvent) => {
    e.preventDefault()
    setReplyTo(null)
  }

  const openLoginModal = () => {
    setLoginModalMessage('Please log in to post a comment.')
    setIsLoginModalOpen(true)
  }

  const onSubmit = async (data: CommentFormData) => {
    if (!user) return

    try {
      // Execute Turnstile
      const token = await executeTurnstile()
      const response = await fetch('/api/verifycaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })

      if (!response.ok) {
        setError('root', {
          type: 'manual',
          message: 'Verification failed. Please try again.',
        })
        return
      }

      // Submit comment
      createComment({
        customerId: user.id,
        blogPostId,
        comment: data.comment,
        responseTo: replyTo?.id || null,
      })
    } catch {
      setError('root', {
        type: 'manual',
        message: 'Failed to post comment. Please try again.',
      })
    }
  }

  // Not logged in
  if (!user) {
    return (
      <>
        <LoginModal
          isLoginOpen={isLoginModalOpen}
          setIsLoginOpen={setIsLoginModalOpen}
          message={loginModalMessage}
          onLoginSuccess={() => {
            // Keep user on current page after successful login
          }}
        />
        <div className="bg-gray-50 p-4" id="comment-form">
          <h2 className="text-2xl font-bold mb-4 text-gray-900">
            Leave a comment
          </h2>
          <p className="text-lg text-gray-700">
            <button
              onClick={openLoginModal}
              className="text-vi-red hover:text-red-700 hover:underline font-semibold transition-colors cursor-pointer"
            >
              Log in
            </button>{' '}
            to post a comment.
          </p>
        </div>
      </>
    )
  }

  return (
    <>
      <LoginModal
        isLoginOpen={isLoginModalOpen}
        setIsLoginOpen={setIsLoginModalOpen}
        message={loginModalMessage}
        onLoginSuccess={() => {
          // Keep user on current page after successful login
        }}
      />
      <div className="bg-gray-50 p-4" id="comment-form">
        {showSuccess && (
          <Alert
            type="success"
            title="Comment Submitted Successfully!"
            className="mb-6"
          >
            Thank you for your comment! It will appear once approved by our
            moderators.
          </Alert>
        )}
        <h2 className="text-2xl font-bold mb-4 text-gray-900">
          Leave a comment
        </h2>

        {replyTo && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">
                Replying to {replyTo.name}'s comment
              </span>
              <br />
              <button
                onClick={cancelReply}
                className="text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium transition-colors"
              >
                Click to cancel and reply to main post
              </button>
            </p>
          </div>
        )}

        <ErrorBox
          errors={
            Object.values(errors)
              .map(error => error?.message)
              .filter(Boolean) as string[]
          }
        />

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label
              htmlFor="comment"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Your comment
            </label>
            <textarea
              id="comment"
              rows={8}
              className="w-full border border-gray-300 bg-white p-3 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-400 transition-colors resize-vertical"
              placeholder="Share your thoughts on this article..."
              {...register('comment', {
                required: 'Please enter your comment',
                minLength: {
                  value: 3,
                  message: 'Comment must be at least 3 characters long',
                },
                maxLength: {
                  value: 5000,
                  message: 'Comment must not exceed 5000 characters',
                },
              })}
            />
          </div>

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm text-gray-600 leading-relaxed">
                <span className="font-semibold text-gray-700">
                  Comment Guidelines:
                </span>
                <br />
                All comments are moderated. Please keep your posts useful and
                interesting. Text only - no HTML or links allowed. We cannot
                answer customer service requests here.
              </p>
                          </div>

            <div className="flex-shrink-0">
              <Button
                type="submit"
                color="green"
                disabled={isPending}
                className="w-full md:w-auto px-8 py-3"
              >
                {isPending ? (
                  <span className="flex items-center gap-2">
                    <ArrowPathIcon className="animate-spin h-4 w-4" />
                    Submitting...
                  </span>
                ) : (
                  'Submit Comment'
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}
